import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { RaRecord } from 'react-admin';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminRpcClient } from '@/providers/backendClient';

const truncateMiddle = (s: string, max = 44) => {
  if (s.length <= max) {
    return s;
  }
  const keep = Math.floor((max - 1) / 2);
  return `${s.slice(0, keep)}…${s.slice(s.length - keep)}`;
};

type InspectEnrollment = {
  speakerLabel?: string;
  enrollmentBlobPath?: string;
  enrolledFromServerBatchIndex?: number;
  sampleStartSec?: number;
  sampleEndSec?: number;
  speakerRole?: string;
};

type InspectSegment = {
  speaker?: string | null;
  start?: number | null;
  end?: number | null;
  id?: number | null;
  plaintextWordCount?: number | null;
  textCipherByteLength?: number | null;
};

type InspectWord = {
  start?: number | null;
  end?: number | null;
  wordPlaintextWordCount?: number | null;
  wordCipherByteLength?: number | null;
};

type InspectTranscriptionMetadata = {
  speakers?: { input?: string[]; output?: string[] };
  plaintextWordCount?: number | null;
  textLinePlaintextWordCount?: number | null;
  textLineCipherByteLength?: number | null;
  segments?: InspectSegment[];
  words?: InspectWord[];
};

type InspectBatch = {
  index?: number;
  status?: string;
  recordingIds?: string[];
  fileIndexes?: number[];
  durationMsApprox?: number;
  error?: string | null;
  transcriptionMetadata?: InspectTranscriptionMetadata | null;
};

type PipelineInspect = {
  batches?: InspectBatch[];
  enrolledSpeakers?: InspectEnrollment[];
};

type PipelineSummary = {
  batchCount?: number;
  enrollmentCount?: number;
  lastBatchStatus?: string | null;
};

type RpcInspectResult = {
  success?: boolean;
  message?: string;
  transcriptionPipelineInspect?: PipelineInspect | null;
  transcriptionPipelineSummary?: PipelineSummary | null;
  wordsTruncated?: boolean;
  maxWordRows?: number;
};

export const TranscriptionPipelineInspectDialog = ({
  sessionNote,
}: {
  sessionNote: RaRecord;
}) => {
  const [open, setOpen] = useState(false);
  const onClose = useCallback(() => setOpen(false), []);

  const sessionNoteId = String(sessionNote.id);
  const isDeleted = Boolean(sessionNote.deletedAt);

  const [inspect, setInspect] = useState<PipelineInspect | null>(
    null,
  );
  const [summary, setSummary] = useState<PipelineSummary | null>(
    null,
  );
  const [wordsTruncated, setWordsTruncated] = useState(false);
  const [maxWordRows, setMaxWordRows] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void adminRpcClient(
      'PUBLIC_ADMIN_therapistSessionNotes_transcriptionPipelineInspect',
      [sessionNoteId, isDeleted],
    )
      .then((res: RpcInspectResult) => {
        if (cancelled) {
          return;
        }
        if (res?.success === false) {
          setLoadError(String(res.message ?? 'Request failed'));
          setInspect(null);
          setSummary(null);
          setWordsTruncated(false);
          setMaxWordRows(null);
          return;
        }
        setSummary(
          (res.transcriptionPipelineSummary as PipelineSummary) ??
            null,
        );
        setInspect(
          (res.transcriptionPipelineInspect as PipelineInspect) ??
            null,
        );
        setWordsTruncated(Boolean(res.wordsTruncated));
        setMaxWordRows(
          typeof res.maxWordRows === 'number'
            ? res.maxWordRows
            : null,
        );
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        const msg =
          err instanceof Error
            ? err.message
            : String(err ?? 'Unknown error');
        setLoadError(msg);
        setInspect(null);
        setSummary(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, sessionNoteId, isDeleted]);

  const batches = inspect?.batches ?? [];
  const enrollments = inspect?.enrolledSpeakers ?? [];

  const hasContent = useMemo(
    () => batches.length > 0 || enrollments.length > 0,
    [batches.length, enrollments.length],
  );

  const copyIds = useCallback((ids: string[] | undefined) => {
    if (!ids?.length) {
      return;
    }
    void navigator.clipboard
      .writeText(ids.join(', '))
      .catch(() => {});
  }, []);

  return (
    <>
      <Tooltip title="Transcription pipeline (loads on open)">
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          aria-label="Open transcription pipeline inspector"
        >
          <AccountTreeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Transcription pipeline</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {loading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading…
                </Typography>
              </Stack>
            )}
            {loadError && (
              <Typography color="error" variant="body2">
                {loadError}
              </Typography>
            )}
            {wordsTruncated && maxWordRows != null && (
              <Typography variant="caption" color="text.secondary">
                Word-level rows are capped at {maxWordRows} total
                across batches for this response.
              </Typography>
            )}
            {!loading && !loadError && (
              <>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Chip
                    size="small"
                    label={`Batches: ${summary?.batchCount ?? batches.length}`}
                  />
                  <Chip
                    size="small"
                    label={`Enrollments: ${summary?.enrollmentCount ?? enrollments.length}`}
                  />
                  {summary?.lastBatchStatus != null &&
                    summary.lastBatchStatus !== '' && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Last batch status: ${summary.lastBatchStatus}`}
                      />
                    )}
                </Stack>

                {!hasContent && (
                  <Typography color="text.secondary">
                    No transcription pipeline on this note (text-only
                    or legacy row).
                  </Typography>
                )}

                {enrollments.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Enrollments
                    </Typography>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Speaker</TableCell>
                            <TableCell>From batch</TableCell>
                            <TableCell>Sample (s)</TableCell>
                            <TableCell>Blob path</TableCell>
                            <TableCell>Role</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {enrollments.map((e, i) => (
                            <TableRow key={i}>
                              <TableCell>{e.speakerLabel}</TableCell>
                              <TableCell>
                                {e.enrolledFromServerBatchIndex ??
                                  '—'}
                              </TableCell>
                              <TableCell>
                                {e.sampleStartSec ?? '—'} –{' '}
                                {e.sampleEndSec ?? '—'}
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: 12,
                                  maxWidth: 280,
                                  wordBreak: 'break-all',
                                }}
                              >
                                {e.enrollmentBlobPath
                                  ? truncateMiddle(
                                      e.enrollmentBlobPath,
                                    )
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                {e.speakerRole ?? '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {batches.map((batch, bi) => {
                  const meta = batch.transcriptionMetadata;
                  const segs = meta?.segments ?? [];
                  const words = meta?.words ?? [];
                  return (
                    <Box key={bi}>
                      <Typography variant="subtitle2" gutterBottom>
                        Batch {batch.index ?? bi}{' '}
                        {batch.status != null
                          ? `· ${batch.status}`
                          : ''}
                        {batch.durationMsApprox != null
                          ? ` · ~${batch.durationMsApprox} ms`
                          : ''}
                      </Typography>
                      {batch.error && (
                        <Typography
                          color="error"
                          variant="body2"
                          sx={{ mb: 1 }}
                        >
                          {batch.error}
                        </Typography>
                      )}
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Recordings:{' '}
                          {batch.recordingIds?.length ?? 0}
                          {batch.fileIndexes != null &&
                            batch.fileIndexes.length > 0 && (
                              <>
                                {' '}
                                · fileIndexes:{' '}
                                {batch.fileIndexes.join(', ')}
                              </>
                            )}
                        </Typography>
                        {!!batch.recordingIds?.length && (
                          <Tooltip title="Copy recording IDs">
                            <IconButton
                              size="small"
                              onClick={() =>
                                copyIds(batch.recordingIds)
                              }
                              aria-label="Copy recording IDs"
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                      {meta && (
                        <Stack spacing={1} sx={{ mb: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Batch plaintext words (stored):{' '}
                            {meta.plaintextWordCount ?? '—'}
                            {meta.textLinePlaintextWordCount !=
                              null && (
                              <>
                                {' '}
                                · Inline API text:{' '}
                                {meta.textLinePlaintextWordCount}{' '}
                                words /{' '}
                                {meta.textLineCipherByteLength ?? '—'}{' '}
                                cipher bytes
                              </>
                            )}
                          </Typography>
                          {meta.speakers && (
                            <Typography
                              variant="caption"
                              component="div"
                            >
                              <strong>Speakers</strong> — in:{' '}
                              {(meta.speakers.input ?? []).join(
                                ', ',
                              ) || '—'}{' '}
                              → out:{' '}
                              {(meta.speakers.output ?? []).join(
                                ', ',
                              ) || '—'}
                            </Typography>
                          )}
                        </Stack>
                      )}
                      {segs.length > 0 && (
                        <TableContainer
                          component={Paper}
                          variant="outlined"
                          sx={{ mb: words.length ? 1 : 0 }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Spk</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>End</TableCell>
                                <TableCell align="right">
                                  Words
                                </TableCell>
                                <TableCell align="right">
                                  Cipher B
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {segs.map((s, si) => (
                                <TableRow key={si}>
                                  <TableCell>
                                    {s.speaker ?? '—'}
                                  </TableCell>
                                  <TableCell>
                                    {s.start ?? '—'}
                                  </TableCell>
                                  <TableCell>
                                    {s.end ?? '—'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {s.plaintextWordCount ?? '—'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {s.textCipherByteLength ?? '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                      {words.length > 0 && (
                        <TableContainer
                          component={Paper}
                          variant="outlined"
                        >
                          <Typography
                            variant="caption"
                            sx={{ px: 1, pt: 1 }}
                            fontWeight={600}
                          >
                            Words (timing + stats)
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Start</TableCell>
                                <TableCell>End</TableCell>
                                <TableCell align="right">
                                  Words
                                </TableCell>
                                <TableCell align="right">
                                  Cipher B
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {words.map((w, wi) => (
                                <TableRow key={wi}>
                                  <TableCell>
                                    {w.start ?? '—'}
                                  </TableCell>
                                  <TableCell>
                                    {w.end ?? '—'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {w.wordPlaintextWordCount ?? '—'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {w.wordCipherByteLength ?? '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  );
                })}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
